import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiZap, FiLoader, FiCheckCircle, FiAlertTriangle, FiArrowRight, FiUser } = FiIcons;

const AppSumoActivate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [user, setUser] = useState(null);

  const code = searchParams.get('code');

  useEffect(() => {
    const init = async () => {
      // Handle simple validation check from AppSumo
      if (!code) {
        setLoading(false);
        return;
      }

      try {
        // Exchange code for license key via our secure Edge Function
        const { data, error: funcError } = await supabase.functions.invoke('appsumo-oauth', {
          body: { code }
        });

        if (funcError || !data.license_key) throw new Error(data?.error || "Invalid activation code");

        setLicenseInfo(data);
        
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        setUser(currentUser);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [code]);

  const handleLinkAccount = async () => {
    if (!user || !licenseInfo) return;
    setLoading(true);
    
    try {
      // 1. Link license to user in DB
      const { error: updateError } = await supabase
        .from('appsumo_licenses_1784567890')
        .update({ user_id: user.id })
        .eq('license_key', licenseInfo.license_key);

      if (updateError) throw updateError;

      // 2. Update profile quota
      const tier = parseInt(licenseInfo.plan_id?.split("_").pop() || "1");
      const slots = tier === 3 ? 10 : tier === 2 ? 5 : 1;
      
      await supabase.rpc('increment_user_quota', {
        user_id: user.id,
        amount: slots - 1 // Subtract initial 1 free slot
      });

      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f4f4]">
        <div className="text-center">
          <SafeIcon icon={FiLoader} className="w-12 h-12 animate-spin mb-4 mx-auto" />
          <p className="font-black uppercase tracking-widest">Validating AppSumo Credits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f4f4] py-20 px-6 flex items-center justify-center">
      <div className="max-w-xl w-full bg-white border-[4px] border-black brutal-shadow-lg p-10 md:p-16">
        {!code ? (
          <div className="text-center">
            <div className="inline-flex items-center justify-center p-4 bg-yellow-300 border-2 border-black mb-8">
              <SafeIcon icon={FiZap} className="w-12 h-12" />
            </div>
            <h1 className="text-4xl font-black uppercase tracking-tighter mb-4">AppSumo Redemption</h1>
            <p className="font-bold text-gray-600 mb-8 uppercase">Ready to activate your ETERA lifetime deal?</p>
            <a 
              href="https://appsumo.com/account/redemption" 
              className="inline-flex items-center gap-3 bg-black text-white border-[3px] border-black px-8 py-4 font-black uppercase brutal-hover transition-all"
            >
              Go to AppSumo <SafeIcon icon={FiArrowRight} />
            </a>
          </div>
        ) : error ? (
          <div className="text-center">
            <SafeIcon icon={FiAlertTriangle} className="w-16 h-16 text-red-600 mb-6 mx-auto" />
            <h2 className="text-3xl font-black uppercase mb-4">Activation Error</h2>
            <p className="font-bold text-red-600 mb-8">{error}</p>
            <Link to="/" className="font-black underline decoration-2 uppercase">Back to Home</Link>
          </div>
        ) : (
          <div className="text-center">
            <div className="bg-green-100 border-4 border-black p-6 mb-8 text-left">
              <div className="flex items-center gap-3 mb-4">
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-600" />
                <h2 className="text-2xl font-black uppercase tracking-tight">License Found!</h2>
              </div>
              <div className="space-y-2 font-bold uppercase text-sm">
                <p>Key: <span className="font-black">{licenseInfo.license_key}</span></p>
                <p>Status: <span className="bg-black text-white px-2">{licenseInfo.license_status}</span></p>
              </div>
            </div>

            {user ? (
              <div className="space-y-6">
                <p className="font-bold text-lg mb-8 uppercase">
                  Loggeed in as <span className="bg-black text-white px-2">{user.email}</span>. 
                  Ready to link your lifetime slots?
                </p>
                <button 
                  onClick={handleLinkAccount}
                  className="w-full bg-black text-white border-[3px] border-black py-5 font-black text-xl uppercase brutal-shadow-sm brutal-hover transition-all flex items-center justify-center gap-3"
                >
                  Confirm & Link <SafeIcon icon={FiZap} />
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <p className="font-bold text-lg mb-8 uppercase">Please sign in to link your AppSumo license.</p>
                <Link 
                  to="/login"
                  state={{ from: { pathname: `/appsumo/activate?code=${code}` } }}
                  className="w-full bg-black text-white border-[3px] border-black py-5 font-black text-xl uppercase brutal-shadow-sm brutal-hover transition-all flex items-center justify-center gap-3"
                >
                  Log In to Redeem <SafeIcon icon={FiUser} />
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppSumoActivate;